import {Translation} from "../../translations/types";
import {Locale, translations, locales} from "../../translations";

let translation: Translation | null = null;

/**
 * Löst einen Pfad ("containers.x.Y.key") gegen den aktuell geladenen
 * Übersetzungsbaum auf. Treu zum historischen Proxy: es wird nur abgestiegen,
 * wenn das Segment existiert — andernfalls bleibt der aktuelle Knoten stehen
 * (ein unbekanntes Segment löst also auf seinen Eltern-Knoten auf).
 */
const resolvePath = (path: string[]): unknown => {
    let node: unknown = translation;
    for (let i = 0; i < path.length; i++) {
        if (node !== null && typeof node === "object" && Reflect.has(node as object, path[i])) {
            node = Reflect.get(node as object, path[i]);
        }
    }
    return node;
};

/** Fallback-String bei fehlender Übersetzung — Format unverändert zur alten Implementierung. */
const buildFallback = (path: string[], args: unknown[]): string =>
    "i18n" + path.map((segment) => "." + segment).join("") + "(" + (args.length === 0 ? "" : JSON.stringify(args)) + ")";

/**
 * Zustandsloser i18n-Proxy: JEDER Property-Zugriff gibt einen NEUEN (pro Segment
 * gecachten) Kind-Proxy zurück, der seinen eigenen unveränderlichen Pfad trägt.
 * Es gibt keinen geteilten, mutierbaren Pfad-Zustand mehr.
 *
 * Folge: Captures wie `const tr = i18n.containers.x.Y; tr.key1(); tr.key2();`
 * sind INHÄRENT SICHER — jeder Aufruf löst unabhängig ab seinem eigenen Pfad auf.
 * Die frühere Vergiftung (ein Capture ohne abschließenden Aufruf ließ den
 * globalen Zustand verschmutzt zurück und ALLE folgenden Aufrufe der ganzen App
 * renderten rohe Keys) ist damit konstruktiv unmöglich.
 *
 * react-hot-loader-Falle: Steht `const t = i18n.<pfad>` auf Modulebene in einer
 * `.tsx`, registriert RHL das Binding und baut per `eval('class ' + t.name ...)`
 * einen Hot-Proxy. `t.name` muss weiterhin aufrufbar bleiben (echte Keys wie
 * `i18n.views.TableColumnTitles.name()` existieren!), darf aber beim
 * String-Coercen KEINEN punktierten Pfad ergeben (sonst `SyntaxError: Unexpected
 * token '.'`). Deshalb liefert `Symbol.toPrimitive` einen punktlosen Bezeichner.
 */
const makeProxy = (path: string[]): Translation => {
    const cache: {[segment: string]: Translation} = {};
    const proxy = (new Proxy(() => undefined, {
        get: (target, key) => {
            if (typeof key === "symbol") {
                // String-Coercion (`${i18n…}`, `+`, String()) OHNE Aufruf: löst — wie
                // der alte Proxy — ein Blatt auf und ruft es parameterlos auf. Wirft
                // eine parametrisierte Message dabei (fehlende Args), oder ist der Pfad
                // kein Blatt (z.B. RHL coerct `i18n.containers.x.Y`), bleibt es beim
                // punktlosen Bezeichner "i18n" → hält modulglobale Bindings RHL-sicher.
                if (key === Symbol.toPrimitive) {
                    return () => {
                        try {
                            const node = resolvePath(path);
                            if (translation !== null && typeof node === "function") {
                                return (node as (...a: unknown[]) => unknown).call(null);
                            }
                        } catch (e) {
                            // parametrisierte Message ohne Args → Fallback
                        }
                        return "i18n";
                    };
                }
                // Andere well-known Symbole (iterator, toStringTag, then, …) NIE als
                // Übersetzungssegment behandeln.
                return Reflect.get(target, key);
            }
            if (cache[key] === undefined) {
                cache[key] = makeProxy(path.concat(key));
            }
            return cache[key];
        },
        apply: (target, context, args) => {
            const node = resolvePath(path);
            return translation !== null && typeof node === "function"
                ? (node as (...a: unknown[]) => unknown).apply(context, args)
                : buildFallback(path, args);
        },
    }) as unknown) as Translation;
    return proxy;
};

const i18n = makeProxy([]);

export const isSupportedLocale = (locale: string): locale is Locale => {
    return locales.includes(locale);
};

export const setTranslation = (newTranslation: Translation) => {
    translation = newTranslation;
};

export const fetchTranslation = async (newLocale: Locale) => {
    return translations[newLocale]();
};

export default i18n;
